import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { 
  Flame, Search, Plus, Clock, Users, Hash, MessageCircle, ArrowUp, ArrowDown, 
  Lock, Pencil, Trash2, TrendingUp, FileText, Folder, Box, AtSign, X, Check, Paperclip,
  ChevronRight, Share2, Bell, Settings, Bot, Home
} from 'lucide-react';
import AppShell from '../components/AppShell';
import SelectMenu from '../components/SelectMenu';
import Modal from '../components/Modal';
import ImageUploadButton from '../components/ImageUploadButton';
import { apiClient } from '../apiClient';
import AuthContext from '../context/AuthContext';
import { SKOOL_SIGNUP_URL } from '../config/externalLinks';
import { buildProfilePath } from '../utils/profileRoutes';

const FEED_TABS = [
  { value: 'hot', label: 'Trending', icon: Flame },
  { value: 'new', label: 'New', icon: Clock },
  { value: 'top', label: 'Top', icon: TrendingUp },
  { value: 'following', label: 'Following', icon: Users }
];

const TIME_WINDOWS = [
  { value: '24h', label: 'Today' },
  { value: '7d', label: 'This Week' },
  { value: '30d', label: 'This Month' },
  { value: 'all', label: 'All Time' }
];

function SocialHubLayout({ children }) {
  const { user, loading, logout } = React.useContext(AuthContext);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (user) {
    return <AppShell onLogout={logout} user={user}>{children}</AppShell>;
  }

  return <div className="min-h-screen hub-public-layout">{children}</div>;
}

// Breadcrumb Navigation
function Breadcrumbs({ items }) {
  return (
    <nav className="hub-breadcrumbs">
      <Link to="/social" className="hub-breadcrumb-item">
        <Home className="w-4 h-4" />
        <span>Social Hub</span>
      </Link>
      {items.map((item, index) => (
        <React.Fragment key={index}>
          <ChevronRight className="w-4 h-4 hub-breadcrumb-sep" />
          {item.to ? (
            <Link to={item.to} className="hub-breadcrumb-item">
              {item.icon && <item.icon className="w-4 h-4" />}
              <span>{item.label}</span>
            </Link>
          ) : (
            <span className="hub-breadcrumb-item current">
              {item.icon && <item.icon className="w-4 h-4" />}
              <span>{item.label}</span>
            </span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

function formatTimeAgo(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
}

function formatBytes(sizeBytes) {
  const value = Number(sizeBytes || 0);
  if (!value) return '';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function UserAvatar({ user, size = 'md' }) {
  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-10 h-10 text-base'
  };
  
  const initial = (user?.name || user?.username || 'R')[0].toUpperCase();
  
  return (
    <div className={`hub-avatar ${sizeClasses[size]}`}>
      {user?.avatar_url ? (
        <img src={user.avatar_url} alt={user.name || 'User'} />
      ) : (
        <span>{initial}</span>
      )}
    </div>
  );
}

function VoteButtons({ score, onVote, userVote, vertical = false }) {
  return (
    <div className={`hub-vote-buttons ${vertical ? 'vertical' : 'horizontal'}`}>
      <button 
        type="button" 
        className={`vote-btn ${userVote === 1 ? 'active upvote' : ''}`}
        onClick={(e) => { e.stopPropagation(); onVote(1); }}
        aria-label="Upvote"
      >
        <ArrowUp className="w-4 h-4" />
      </button>
      <span className="vote-score">{score ?? 0}</span>
      <button 
        type="button" 
        className={`vote-btn ${userVote === -1 ? 'active downvote' : ''}`}
        onClick={(e) => { e.stopPropagation(); onVote(-1); }}
        aria-label="Downvote"
      >
        <ArrowDown className="w-4 h-4" />
      </button>
    </div>
  );
}

function PostAttachmentList({ attachments = [], compact = false, editable = false, onDelete }) {
  if (!attachments.length) return null;

  return (
    <div className={`hub-attachment-list ${compact ? 'compact' : ''}`}>
      {attachments.map((attachment) => {
        const isImage = (attachment.content_type || '').startsWith('image/');
        return (
          <div key={attachment.attachment_id} className="hub-attachment-item">
            {isImage && attachment.view_url ? (
              <a href={attachment.view_url} target="_blank" rel="noreferrer" className="hub-attachment-thumb">
                <img src={attachment.view_url} alt={attachment.name} loading="lazy" />
              </a>
            ) : (
              <div className="hub-attachment-thumb file">
                <Paperclip className="w-4 h-4" />
              </div>
            )}
            <div className="hub-attachment-meta">
              <a href={attachment.download_url || attachment.view_url || '#'} target="_blank" rel="noreferrer" className="hub-attachment-name">
                {attachment.name}
              </a>
              <span className="hub-attachment-subtext">
                {attachment.kind === 'image' ? 'Image' : 'File'}
                {attachment.size_bytes ? ` • ${formatBytes(attachment.size_bytes)}` : ''}
              </span>
            </div>
            {editable && (
              <button type="button" className="hub-action-btn danger" onClick={() => onDelete?.(attachment)}>
                <Trash2 className="w-3 h-3" />
                <span>Remove</span>
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PostCard({ post, onVote, showCommunity = true }) {
  const navigate = useNavigate();
  const { user } = React.useContext(AuthContext);
  
  const handleVote = async (vote) => {
    if (!user) return;
    try {
      await apiClient.post(`/api/hub/posts/${post.post_id}/vote`, { vote });
      if (onVote) onVote(post.post_id, vote);
    } catch (err) {
      console.error('Vote failed:', err);
    }
  };
  
  return (
    <article 
      className="hub-post-card"
      onClick={() => navigate(`/social/posts/${post.post_id}`)}
    >
      <div className="hub-post-vote-col">
        <VoteButtons 
          score={post.score} 
          onVote={handleVote}
          userVote={post.user_vote}
          vertical
        />
      </div>
      
      <div className="hub-post-content">
        <div className="hub-post-meta">
          {showCommunity && (
            <>
              <Link 
                to={`/social/communities/${post.guild_id}`}
                className="hub-community-link"
                onClick={(e) => e.stopPropagation()}
              >
                <Hash className="w-3 h-3" />
                {post.guild_name || 'General'}
              </Link>
              <span className="meta-dot">•</span>
            </>
          )}
          <Link 
            to={buildProfilePath(post.author)}
            className="hub-author-link"
            onClick={(e) => e.stopPropagation()}
          >
            <UserAvatar user={post.author} size="sm" />
            {post.author?.username || post.author?.name || 'Rebel'}
          </Link>
          <span className="meta-dot">•</span>
          <span className="hub-time">{formatTimeAgo(post.created_at)}</span>
          {post.is_pinned && <span className="hub-badge pinned">Pinned</span>}
          {post.is_locked && <span className="hub-badge locked"><Lock className="w-3 h-3" /></span>}
        </div>
        
        <h3 className="hub-post-title">{post.title}</h3>
        
        {post.body_md && (
          <p className="hub-post-excerpt">{post.body_md.slice(0, 200)}{post.body_md.length > 200 ? '...' : ''}</p>
        )}
        
        {post.image_url && (
          <div className="hub-post-image">
            <img src={post.image_url} alt="" loading="lazy" />
          </div>
        )}

        {Array.isArray(post.attachments) && post.attachments.length > 0 && (
          <PostAttachmentList attachments={post.attachments} compact />
        )}
        
        {post.embed?.data && (
          <div className="hub-embed-card" onClick={(e) => e.stopPropagation()}>
            <div className="hub-embed-icon">
              {post.embed.type === 'prompt' && <FileText className="w-4 h-4" />}
              {post.embed.type === 'project' && <Box className="w-4 h-4" />}
              {post.embed.type === 'folder' && <Folder className="w-4 h-4" />}
              {post.embed.type === 'instruction' && <Bot className="w-4 h-4" />}
            </div>
            <div className="hub-embed-info">
              <span className="hub-embed-type">{post.embed.type === 'instruction' ? 'Agent Instructions' : post.embed.type}</span>
              <span className="hub-embed-name">{post.embed.data.name}</span>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-500" />
          </div>
        )}
        
        <div className="hub-post-actions">
          <button className="hub-action-btn">
            <MessageCircle className="w-4 h-4" />
            <span>{post.comment_count ?? 0} comments</span>
          </button>
          {post.attachment_count > 0 && (
            <button className="hub-action-btn" onClick={(e) => e.stopPropagation()}>
              <Paperclip className="w-4 h-4" />
              <span>{post.attachment_count} attachments</span>
            </button>
          )}
          <button className="hub-action-btn" onClick={(e) => e.stopPropagation()}>
            <Share2 className="w-4 h-4" />
            <span>Share</span>
          </button>
        </div>
      </div>
    </article>
  );
}

// Content Picker Modal for linking prompts/projects/folders/instructions
function ContentPickerModal({ open, onClose, onSelect }) {
  const [tab, setTab] = useState('prompt');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    
    // Use library endpoints to get PUBLIC items only
    let endpoint;
    let params = { limit: 50 };
    
    if (tab === 'prompt') {
      endpoint = '/api/library/prompts';
    } else if (tab === 'project') {
      endpoint = '/api/library/projects';
    } else if (tab === 'folder') {
      endpoint = '/api/library/folders';
    } else if (tab === 'instruction') {
      endpoint = '/api/public/agent-instructions';
    }
    
    apiClient.get(endpoint, params)
      .then(data => {
        // Library endpoints return { items: [...] } format for prompts/projects/folders
        // Instructions endpoint returns array directly
        if (Array.isArray(data)) {
          setItems(data);
        } else if (data?.items) {
          setItems(data.items);
        } else {
          setItems([]);
        }
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [open, tab]);

  const filtered = items.filter(item => 
    (item.name || '').toLowerCase().includes(search.toLowerCase())
  );

  const getItemId = (item) => {
    if (tab === 'prompt') return item.prompt_id;
    if (tab === 'project') return item.project_id;
    if (tab === 'instruction') return item.instruction_id;
    return item.folder_id;
  };

  const getIcon = () => {
    if (tab === 'prompt') return <FileText className="w-4 h-4" />;
    if (tab === 'project') return <Box className="w-4 h-4" />;
    if (tab === 'instruction') return <Bot className="w-4 h-4" />;
    return <Folder className="w-4 h-4" />;
  };

  return (
    <Modal
      open={open}
      title="Link Content"
      onClose={onClose}
    >
      <div className="content-picker">
        <div className="content-picker-tabs">
          <button 
            className={`picker-tab ${tab === 'prompt' ? 'active' : ''}`}
            onClick={() => setTab('prompt')}
          >
            <FileText className="w-4 h-4" /> Prompts
          </button>
          <button 
            className={`picker-tab ${tab === 'project' ? 'active' : ''}`}
            onClick={() => setTab('project')}
          >
            <Box className="w-4 h-4" /> Projects
          </button>
          <button 
            className={`picker-tab ${tab === 'folder' ? 'active' : ''}`}
            onClick={() => setTab('folder')}
          >
            <Folder className="w-4 h-4" /> Folders
          </button>
          <button 
            className={`picker-tab ${tab === 'instruction' ? 'active' : ''}`}
            onClick={() => setTab('instruction')}
          >
            <Bot className="w-4 h-4" /> Instructions
          </button>
        </div>
        
        <div className="content-picker-search">
          <Search className="w-4 h-4" />
          <input 
            type="text"
            placeholder={`Search public ${tab === 'instruction' ? 'instructions' : tab + 's'}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        
        <div className="content-picker-list">
          {loading ? (
            <div className="picker-loading"><div className="spinner" /></div>
          ) : filtered.length === 0 ? (
            <div className="picker-empty">
              No public {tab === 'instruction' ? 'instructions' : tab + 's'} found.
            </div>
          ) : (
            filtered.slice(0, 20).map(item => (
              <button
                key={getItemId(item)}
                className="picker-item"
                onClick={() => {
                  onSelect(tab, getItemId(item), item);
                  onClose();
                }}
              >
                <div className="picker-item-icon">
                  {getIcon()}
                </div>
                <div className="picker-item-info">
                  <div className="picker-item-name">{item.name}</div>
                  <div className="picker-item-desc">
                    {item.description || 'No description'}
                    {item.creator && (
                      <span className="picker-item-creator"> • by {item.creator?.username || item.creator?.name}</span>
                    )}
                  </div>
                </div>
                <Check className="w-4 h-4 picker-check" />
              </button>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}

// User Mention Input with autocomplete
function MentionInput({ value, onChange, placeholder, rows = 4, className = '' }) {
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionUsers, setMentionUsers] = useState([]);
  const [cursorPos, setCursorPos] = useState(0);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!mentionSearch || mentionSearch.length < 2) {
      setMentionUsers([]);
      return;
    }
    const timer = setTimeout(() => {
      apiClient.get('/api/users/search', { query: mentionSearch })
        .then(data => setMentionUsers(data || []))
        .catch(() => setMentionUsers([]));
    }, 200);
    return () => clearTimeout(timer);
  }, [mentionSearch]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    const pos = e.target.selectionStart;
    setCursorPos(pos);
    onChange(newValue);

    // Check if we're typing a mention
    const textBefore = newValue.slice(0, pos);
    const mentionMatch = textBefore.match(/@(\w*)$/);
    
    if (mentionMatch) {
      setShowMentions(true);
      setMentionSearch(mentionMatch[1]);
    } else {
      setShowMentions(false);
      setMentionSearch('');
    }
  };

  const insertMention = (user) => {
    const textBefore = value.slice(0, cursorPos);
    const textAfter = value.slice(cursorPos);
    const mentionStart = textBefore.lastIndexOf('@');
    const newText = textBefore.slice(0, mentionStart) + `@${user.username} ` + textAfter;
    onChange(newText);
    setShowMentions(false);
    setMentionSearch('');
    textareaRef.current?.focus();
  };

  return (
    <div className="mention-input-wrapper">
      <textarea
        ref={textareaRef}
        className={`input-glass ${className}`}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        rows={rows}
      />
      {showMentions && mentionUsers.length > 0 && (
        <div className="mention-dropdown">
          {mentionUsers.slice(0, 5).map(user => (
            <button
              key={user.user_id}
              className="mention-option"
              onClick={() => insertMention(user)}
            >
              <UserAvatar user={user} size="sm" />
              <div className="mention-option-info">
                <span className="mention-name">{user.name || user.username}</span>
                <span className="mention-username">@{user.username}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CreatePostModal({ open, onClose, communities, onCreated, preselectedCommunity }) {
  const { user } = React.useContext(AuthContext);
  const [communityId, setCommunityId] = useState(preselectedCommunity || '');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [queuedFiles, setQueuedFiles] = useState([]);
  const [embedType, setEmbedType] = useState('');
  const [embedRef, setEmbedRef] = useState('');
  const [embedPreview, setEmbedPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showContentPicker, setShowContentPicker] = useState(false);

  useEffect(() => {
    if (preselectedCommunity) setCommunityId(preselectedCommunity);
  }, [preselectedCommunity]);

  const handleCreate = async () => {
    if (!user) return;
    if (!communityId) {
      setError('Select a community to post in.');
      return;
    }
    if (!title.trim()) {
      setError('Post title is required.');
      return;
    }
    setSaving(true);
    setError('');
    let createdPost = null;
    try {
      const payload = {
        guild_id: communityId,
        type: 'text',
        title,
        body_md: body,
        url: null,
        image_url: imageUrl || null,
        // Only include embed if one was selected
        embed_type: embedType || null,
        embed_ref_id: embedRef || null
      };
      createdPost = await apiClient.post('/api/hub/posts', payload);
      if (queuedFiles.length > 0) {
        for (const queuedFile of queuedFiles) {
          const formData = new FormData();
          formData.append('file', queuedFile);
          await apiClient.request(`/api/hub/posts/${createdPost.post_id}/attachments`, {
            method: 'POST',
            body: formData
          });
        }
        createdPost = await apiClient.get(`/api/hub/posts/${createdPost.post_id}`);
      }
      onCreated(createdPost);
      // Reset form
      setTitle('');
      setBody('');
      setImageUrl('');
      setQueuedFiles([]);
      setEmbedType('');
      setEmbedRef('');
      setEmbedPreview(null);
      if (!preselectedCommunity) setCommunityId('');
      onClose();
    } catch (err) {
      if (createdPost) {
        onCreated(createdPost);
        onClose();
        return;
      }
      setError(err.message || 'Unable to create post.');
    } finally {
      setSaving(false);
    }
  };

  const handleEmbedSelect = (type, id, item) => {
    setEmbedType(type);
    setEmbedRef(id);
    setEmbedPreview(item);
  };

  const clearEmbed = () => {
    setEmbedType('');
    setEmbedRef('');
    setEmbedPreview(null);
  };

  return (
    <>
      <Modal
        open={open}
        title="Create Post"
        onClose={onClose}
        size="lg"
        footer={(
          <div className="modal-actions">
            <button className="btn-secondary" type="button" onClick={onClose}>Cancel</button>
            <button 
              className="btn-primary" 
              type="button" 
              disabled={saving || !communityId || !title.trim()} 
              onClick={handleCreate}
            >
              {saving ? 'Posting...' : 'Post'}
            </button>
          </div>
        )}
      >
        {error && <div className="error-banner">{error}</div>}
        
        <div className="modal-field">
          <label>Community</label>
          <SelectMenu
            value={communityId}
            options={communities.map((c) => ({ value: c.guild_id, label: c.name }))}
            placeholder="Select a community"
            onChange={setCommunityId}
          />
        </div>
        
        <div className="modal-field">
          <label>Title</label>
          <input 
            className="input-glass" 
            value={title} 
            onChange={(e) => setTitle(e.target.value)} 
            placeholder="An interesting title..."
          />
        </div>
        
        <div className="modal-field">
          <label>Body</label>
          <MentionInput
            value={body}
            onChange={setBody}
            placeholder="Share your thoughts... Use @username to mention others"
            rows={6}
          />
        </div>

        <div className="modal-field">
          <label>External image URL (optional)</label>
          <div className="flex items-center gap-3">
            <input 
              className="input-glass flex-1" 
              value={imageUrl} 
              onChange={(e) => setImageUrl(e.target.value)} 
              placeholder="Paste an image URL if you want to embed one"
            />
          </div>
        </div>

        <div className="modal-field">
          <label>Upload files or images (optional)</label>
          <div className="flex items-center gap-3">
            <ImageUploadButton
              label="Attach files"
              variant="button"
              multiple
              accept="image/*,.pdf,.txt,.md,.csv,.doc,.docx,.zip"
              disabled={saving}
              onSelect={(files) => {
                const nextFiles = Array.isArray(files) ? files : [files];
                setQueuedFiles((prev) => [...prev, ...nextFiles.filter(Boolean)]);
              }}
            />
            <span className="text-xs text-gray-500">Images, PDFs, text docs, and common work files</span>
          </div>
          {queuedFiles.length > 0 && (
            <div className="hub-upload-queue">
              {queuedFiles.map((queuedFile, index) => (
                <div key={`${queuedFile.name}-${queuedFile.size}-${index}`} className="hub-upload-queue-item">
                  <div className="hub-upload-queue-meta">
                    <span className="hub-upload-queue-name">{queuedFile.name}</span>
                    <span className="hub-upload-queue-size">{formatBytes(queuedFile.size)}</span>
                  </div>
                  <button
                    type="button"
                    className="hub-action-btn danger"
                    onClick={() => setQueuedFiles((prev) => prev.filter((_, itemIndex) => itemIndex !== index))}
                  >
                    <X className="w-3 h-3" />
                    <span>Remove</span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-field">
          <label>Link Content (optional)</label>
          <p className="text-xs text-gray-500 mb-2">Share a public prompt, project, folder, or agent instructions</p>
          {embedPreview ? (
            <div className="hub-embed-preview">
              <div className="hub-embed-icon">
                {embedType === 'prompt' && <FileText className="w-4 h-4" />}
                {embedType === 'project' && <Box className="w-4 h-4" />}
                {embedType === 'folder' && <Folder className="w-4 h-4" />}
                {embedType === 'instruction' && <Bot className="w-4 h-4" />}
              </div>
              <div className="hub-embed-info">
                <span className="hub-embed-type">{embedType === 'instruction' ? 'Agent Instructions' : embedType}</span>
                <span className="hub-embed-name">{embedPreview.name}</span>
              </div>
              <button className="btn-icon" onClick={clearEmbed}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button 
              type="button" 
              className="btn-secondary w-full"
              onClick={() => setShowContentPicker(true)}
            >
              <Plus className="w-4 h-4" /> Browse Public Content to Link
            </button>
          )}
        </div>
      </Modal>

      <ContentPickerModal
        open={showContentPicker}
        onClose={() => setShowContentPicker(false)}
        onSelect={handleEmbedSelect}
      />
    </>
  );
}

function CreateCommunityModal({ open, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [rules, setRules] = useState('');
  const [tags, setTags] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Community name is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        rules_md: rules.trim(),
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
        is_public: isPublic
      };
      const created = await apiClient.post('/api/hub/guilds', payload);
      onCreated(created);
      setName('');
      setDescription('');
      setRules('');
      setTags('');
      setIsPublic(true);
      onClose();
    } catch (err) {
      setError(err.message || 'Unable to create community.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Create Community"
      onClose={onClose}
      footer={(
        <div className="modal-actions">
          <button className="btn-secondary" type="button" onClick={onClose}>Cancel</button>
          <button className="btn-primary" type="button" disabled={saving} onClick={handleCreate}>
            {saving ? 'Creating...' : 'Create Community'}
          </button>
        </div>
      )}
    >
      {error && <div className="error-banner">{error}</div>}
      
      <div className="modal-field">
        <label>Name</label>
        <input 
          className="input-glass" 
          value={name} 
          onChange={(e) => setName(e.target.value)} 
          placeholder="e.g., Prompt Engineers"
        />
      </div>
      
      <div className="modal-field">
        <label>Description</label>
        <textarea 
          className="input-glass" 
          rows="3" 
          value={description} 
          onChange={(e) => setDescription(e.target.value)} 
          placeholder="What is this community about?"
        />
      </div>
      
      <div className="modal-field">
        <label>Rules (optional)</label>
        <textarea 
          className="input-glass" 
          rows="3" 
          value={rules} 
          onChange={(e) => setRules(e.target.value)} 
          placeholder="Community guidelines..."
        />
      </div>
      
      <div className="modal-field">
        <label>Tags (comma separated)</label>
        <input 
          className="input-glass" 
          value={tags} 
          onChange={(e) => setTags(e.target.value)} 
          placeholder="prompts, automation, workflows"
        />
      </div>
      
      <div className="modal-field">
        <label>Visibility</label>
        <SelectMenu
          value={isPublic ? 'public' : 'private'}
          options={[
            { value: 'public', label: 'Public', description: 'Anyone can view and join' },
            { value: 'private', label: 'Private', description: 'Invite only' }
          ]}
          onChange={(v) => setIsPublic(v === 'public')}
        />
      </div>
    </Modal>
  );
}

function CommunityCard({ community, compact = false, onJoined }) {
  const navigate = useNavigate();
  const { user } = React.useContext(AuthContext);

  const handleJoin = async (e) => {
    e.stopPropagation();
    if (!user) return;
    try {
      await apiClient.post(`/api/hub/guilds/${community.guild_id}/join`);
      if (onJoined) onJoined(community.guild_id);
    } catch (err) {
      console.error('Join failed:', err);
    }
  };

  if (compact) {
    return (
      <Link to={`/social/communities/${community.guild_id}`} className="hub-community-compact">
        <div className="hub-community-icon">
          <Hash className="w-4 h-4" />
        </div>
        <div className="hub-community-info">
          <span className="hub-community-name">{community.name}</span>
          <span className="hub-community-members">{community.member_count || 0} members</span>
        </div>
      </Link>
    );
  }

  return (
    <div 
      className="hub-community-card"
      onClick={() => navigate(`/social/communities/${community.guild_id}`)}
    >
      <div className="hub-community-header">
        <div className="hub-community-icon lg">
          <Hash className="w-5 h-5" />
        </div>
        {!community.is_public && <Lock className="w-4 h-4 text-gray-500" />}
      </div>
      <h3 className="hub-community-title">{community.name}</h3>
      <p className="hub-community-desc">{community.description || 'No description yet.'}</p>
      <div className="hub-community-meta">
        <span>{community.member_count || 0} members</span>
        <span className="meta-dot">•</span>
        <span>{formatTimeAgo(community.created_at)}</span>
      </div>
      {user && !community.is_member && (
        <button className="btn-primary btn-compact mt-3" onClick={handleJoin}>
          Join Community
        </button>
      )}
    </div>
  );
}

export function SocialHubHome() {
  const { user } = React.useContext(AuthContext);
  const [feedType, setFeedType] = useState('hot');
  const [timeWindow, setTimeWindow] = useState('7d');
  const [posts, setPosts] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showCreateCommunity, setShowCreateCommunity] = useState(false);

  const markJoined = (guildId) => {
    setCommunities((prev) => prev.map((c) => (
      c.guild_id === guildId ? { ...c, is_member: true, member_count: (c.member_count || 0) + 1 } : c
    )));
    setSearchResults((prev) => {
      if (!prev?.guilds) return prev;
      return {
        ...prev,
        guilds: prev.guilds.map((c) => (
          c.guild_id === guildId ? { ...c, is_member: true, member_count: (c.member_count || 0) + 1 } : c
        ))
      };
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { feed_type: feedType };
      if (feedType === 'top') params.window = timeWindow;
      
      const [feedData, communityData] = await Promise.all([
        apiClient.get('/api/hub/feed', params),
        apiClient.get('/api/hub/guilds')
      ]);
      setPosts(feedData || []);
      setCommunities(communityData || []);
    } catch (err) {
      setError(err.message || 'Unable to load hub.');
    } finally {
      setLoading(false);
    }
  }, [feedType, timeWindow]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!search || search.length < 2) {
      setSearchResults(null);
      return;
    }
    const timer = setTimeout(() => {
      apiClient.get('/api/hub/search', { query: search })
        .then(setSearchResults)
        .catch(() => setSearchResults(null));
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleVote = (postId, vote) => {
    setPosts(prev => prev.map(p => 
      p.post_id === postId 
        ? { ...p, score: (p.score || 0) + (p.user_vote ? vote - p.user_vote : vote), user_vote: vote }
        : p
    ));
  };

  return (
    <SocialHubLayout>
      <div className="hub-page">
        {/* Page Title */}
        <header className="hub-page-title">
          <h1>Social Hub</h1>
          <p>Join the conversation. Share prompts, projects, and ideas with the Rebel community.</p>
        </header>

        {/* Communities Section - Featured at Top */}
        <section className="hub-communities-section">
          <div className="hub-section-header">
            <h2><Hash className="w-5 h-5" /> Communities</h2>
            <Link to="/social/communities" className="hub-section-link">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="hub-communities-row">
            {communities.slice(0, 6).map((c) => (
              <CommunityCard key={c.guild_id} community={c} />
            ))}
            {communities.length === 0 && !loading && (
              <div className="hub-empty-inline">
                <p>No communities yet. Be the first to create one!</p>
                {user && (
                  <button className="btn-primary btn-compact" onClick={() => setShowCreateCommunity(true)}>
                    <Plus className="w-4 h-4" /> Create Community
                  </button>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Create Actions */}
        {user && (
          <div className="hub-create-actions">
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              <Plus className="w-4 h-4" /> New Post
            </button>
            <button className="btn-secondary" onClick={() => setShowCreateCommunity(true)}>
              <Hash className="w-4 h-4" /> New Community
            </button>
          </div>
        )}

        {/* Search & Filters */}
        <section className="hub-toolbar">
          <div className="hub-search">
            <Search className="w-4 h-4" />
            <input
              type="text"
              placeholder="Search posts, communities, users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="hub-search-clear" onClick={() => setSearch('')}>
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          <div className="hub-filters">
            <div className="hub-tabs">
              {FEED_TABS.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.value}
                    className={`hub-tab ${feedType === tab.value ? 'active' : ''}`}
                    onClick={() => setFeedType(tab.value)}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="tab-label">{tab.label}</span>
                  </button>
                );
              })}
            </div>
            
            {feedType === 'top' && (
              <SelectMenu
                value={timeWindow}
                options={TIME_WINDOWS}
                onChange={setTimeWindow}
                compact
              />
            )}
          </div>
        </section>

        {/* Search Results */}
        {searchResults && (
          <section className="hub-search-results">
            <div className="hub-search-section">
              <h3>Communities</h3>
              {(searchResults.guilds || []).length === 0 ? (
                <p className="text-gray-500 text-sm">No communities found</p>
              ) : (
                <div className="hub-search-list">
                  {searchResults.guilds.map((c) => (
                    <CommunityCard key={c.guild_id} community={c} compact onJoined={markJoined} />
                  ))}
                </div>
              )}
            </div>
            <div className="hub-search-section">
              <h3>Posts</h3>
              {(searchResults.posts || []).length === 0 ? (
                <p className="text-gray-500 text-sm">No posts found</p>
              ) : (
                <div className="hub-search-list">
                  {searchResults.posts.slice(0, 5).map((p) => (
                    <Link key={p.post_id} to={`/social/posts/${p.post_id}`} className="hub-search-item">
                      <span className="font-medium">{p.title}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <div className="hub-search-section">
              <h3>Users</h3>
              {(searchResults.users || []).length === 0 ? (
                <p className="text-gray-500 text-sm">No users found</p>
              ) : (
                <div className="hub-search-list">
                  {searchResults.users.map((u) => (
                    <Link key={u.user_id} to={buildProfilePath(u)} className="hub-search-item user">
                      <UserAvatar user={u} size="sm" />
                      <span>{u.name || u.username}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {/* Feed Section */}
        <section className="hub-feed-section">
          <div className="hub-section-header">
            <h2><Flame className="w-5 h-5" /> {FEED_TABS.find(t => t.value === feedType)?.label || 'Feed'}</h2>
          </div>

          {error && <div className="error-banner">{error}</div>}
          
          {loading ? (
            <div className="hub-loading">
              <div className="spinner" />
            </div>
          ) : posts.length === 0 ? (
            <div className="hub-empty">
              <MessageCircle className="w-12 h-12 text-gray-600" />
              <h3>No posts yet</h3>
              <p>Be the first to share something with the community.</p>
              {user && (
                <button className="btn-primary mt-4" onClick={() => setShowCreate(true)}>
                  Create Post
                </button>
              )}
            </div>
          ) : (
            <div className="hub-post-list">
              {posts.map((post) => (
                <PostCard 
                  key={post.post_id} 
                  post={{ ...post, guild_name: communities.find((c) => c.guild_id === post.guild_id)?.name }}
                  onVote={handleVote}
                />
              ))}
            </div>
          )}
        </section>

        {/* Sidebar for guidelines (only on desktop) */}
        <aside className="hub-home-sidebar">
          <div className="hub-sidebar-card">
            <h3>Community Guidelines</h3>
            <ul className="hub-guidelines">
              <li><Check className="w-4 h-4" /> Share working prompts and real results</li>
              <li><Check className="w-4 h-4" /> Credit original creators</li>
              <li><Check className="w-4 h-4" /> Be constructive in feedback</li>
              <li><X className="w-4 h-4" /> No spam or self-promotion</li>
            </ul>
          </div>

          {!user && (
            <div className="hub-sidebar-card cta">
              <h3>Join the Community</h3>
              <p>Create an account to post, comment, and vote.</p>
              <a href={SKOOL_SIGNUP_URL} target="_blank" rel="noreferrer" className="btn-primary w-full mt-3 text-center">Sign Up Free</a>
            </div>
          )}
        </aside>
      </div>

      <CreatePostModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        communities={communities}
        onCreated={(post) => setPosts((prev) => [post, ...prev])}
      />
      <CreateCommunityModal
        open={showCreateCommunity}
        onClose={() => setShowCreateCommunity(false)}
        onCreated={(c) => setCommunities((prev) => [c, ...prev])}
      />
    </SocialHubLayout>
  );
}

export function SocialGuildDirectory() {
  const { user } = React.useContext(AuthContext);
  const [search, setSearch] = useState('');
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const markJoined = (guildId) => {
    setCommunities((prev) => prev.map((c) => (
      c.guild_id === guildId ? { ...c, is_member: true, member_count: (c.member_count || 0) + 1 } : c
    )));
  };

  const load = useCallback(async () => {
    setLoading(true);
    const data = await apiClient.get('/api/hub/guilds', { search: search || undefined });
    setCommunities(data || []);
    setLoading(false);
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  return (
    <SocialHubLayout>
      <div className="hub-page">
        <Breadcrumbs items={[{ label: 'Communities', icon: Hash }]} />
        
        <section className="hub-page-header">
          <div>
            <h1>Communities</h1>
            <p>Find your people. Join communities built for AI builders and prompt engineers.</p>
          </div>
          {user && (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              <Plus className="w-4 h-4" /> Create Community
            </button>
          )}
        </section>

        <div className="hub-search standalone">
          <Search className="w-4 h-4" />
          <input
            type="text"
            placeholder="Search communities..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="hub-loading"><div className="spinner" /></div>
        ) : communities.length === 0 ? (
          <div className="hub-empty">
            <Hash className="w-12 h-12 text-gray-600" />
            <h3>No communities found</h3>
            <p>Try a different search or create the first one!</p>
          </div>
        ) : (
          <div className="hub-community-grid">
            {communities.map((c) => (
              <CommunityCard key={c.guild_id} community={c} onJoined={markJoined} />
            ))}
          </div>
        )}
      </div>

      <CreateCommunityModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(c) => setCommunities((prev) => [c, ...prev])}
      />
    </SocialHubLayout>
  );
}

export function SocialGuildPage() {
  const { guildId } = useParams();
  const { user } = React.useContext(AuthContext);
  const [community, setCommunity] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const [communityData, feedData] = await Promise.all([
          apiClient.get(`/api/hub/guilds/${guildId}`),
          apiClient.get('/api/hub/feed', { guild_id: guildId })
        ]);
        if (!active) return;
        setCommunity(communityData);
        setPosts(feedData || []);
      } catch (err) {
        console.error('Load failed:', err);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [guildId]);

  const handleJoinLeave = async () => {
    if (!user || !community) return;
    try {
      if (community.is_member) {
        await apiClient.post(`/api/hub/guilds/${community.guild_id}/leave`);
        setCommunity((prev) => ({ ...prev, is_member: false }));
      } else {
        await apiClient.post(`/api/hub/guilds/${community.guild_id}/join`);
        setCommunity((prev) => ({ ...prev, is_member: true }));
      }
    } catch (err) {
      console.error('Join/leave failed:', err);
    }
  };

  const handleVote = (postId, vote) => {
    setPosts(prev => prev.map(p => 
      p.post_id === postId 
        ? { ...p, score: (p.score || 0) + (p.user_vote ? vote - p.user_vote : vote), user_vote: vote }
        : p
    ));
  };

  if (loading) {
    return (
      <SocialHubLayout>
        <div className="hub-loading"><div className="spinner" /></div>
      </SocialHubLayout>
    );
  }

  if (!community) {
    return (
      <SocialHubLayout>
        <div className="hub-page">
          <Breadcrumbs items={[{ label: 'Communities', to: '/social/communities', icon: Hash }]} />
          <div className="hub-empty">
            <h3>Community not found</h3>
            <Link to="/social/communities" className="btn-secondary mt-4">Browse Communities</Link>
          </div>
        </div>
      </SocialHubLayout>
    );
  }

  return (
    <SocialHubLayout>
      <div className="hub-page">
        <Breadcrumbs items={[
          { label: 'Communities', to: '/social/communities', icon: Hash },
          { label: community.name }
        ]} />

        {/* Community Header */}
        <section className="hub-community-hero">
          <div className="hub-community-hero-icon">
            <Hash className="w-8 h-8" />
          </div>
          <div className="hub-community-hero-info">
            <h1>{community.name}</h1>
            <p>{community.description || 'No description yet.'}</p>
            <div className="hub-community-stats">
              <span><Users className="w-4 h-4" /> {community.member_count || 0} members</span>
              <span><Clock className="w-4 h-4" /> Created {formatTimeAgo(community.created_at)}</span>
            </div>
          </div>
          <div className="hub-community-hero-actions">
            {user && (
              <>
                <button 
                  className={community.is_member ? 'btn-secondary' : 'btn-primary'}
                  onClick={handleJoinLeave}
                >
                  {community.is_member ? 'Leave' : 'Join'}
                </button>
                {community.is_member && (
                  <button className="btn-primary" onClick={() => setShowCreate(true)}>
                    <Plus className="w-4 h-4" /> New Post
                  </button>
                )}
              </>
            )}
          </div>
        </section>

        {/* Rules */}
        {community.rules_md && (
          <section className="hub-community-rules">
            <h3><Settings className="w-4 h-4" /> Community Rules</h3>
            <p>{community.rules_md}</p>
          </section>
        )}

        {/* Posts */}
        <section className="hub-community-posts">
          <div className="hub-section-header">
            <h2><MessageCircle className="w-5 h-5" /> Posts in {community.name}</h2>
          </div>
          
          {posts.length === 0 ? (
            <div className="hub-empty">
              <MessageCircle className="w-10 h-10 text-gray-600" />
              <h3>No posts yet</h3>
              <p>Start the conversation in this community.</p>
              {user && community.is_member && (
                <button className="btn-primary mt-4" onClick={() => setShowCreate(true)}>
                  Create First Post
                </button>
              )}
            </div>
          ) : (
            <div className="hub-post-list">
              {posts.map((post) => (
                <PostCard 
                  key={post.post_id} 
                  post={{ ...post, guild_name: community.name }}
                  onVote={handleVote}
                  showCommunity={false}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      <CreatePostModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        communities={[community]}
        preselectedCommunity={community.guild_id}
        onCreated={(post) => setPosts((prev) => [post, ...prev])}
      />
    </SocialHubLayout>
  );
}

// Comment components
function CommentItem({ comment, depth = 0, onVote, onReply, onEdit, onDelete, currentUserId }) {
  const [isReplying, setIsReplying] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [editText, setEditText] = useState(comment.body_md || '');
  const maxDepth = 4;

  const handleVote = async (vote) => {
    if (onVote) onVote(comment.comment_id, vote);
  };

  const handleSubmitReply = () => {
    if (!replyText.trim()) return;
    onReply(comment.comment_id, replyText);
    setReplyText('');
    setIsReplying(false);
  };

  const handleSubmitEdit = () => {
    if (!editText.trim()) return;
    onEdit(comment.comment_id, editText);
    setIsEditing(false);
  };

  return (
    <div className={`hub-comment ${depth > 0 ? 'nested' : ''}`} style={{ '--depth': Math.min(depth, maxDepth) }}>
      <div className="hub-comment-main">
        <div className="hub-comment-left">
          <UserAvatar user={comment.author} size="sm" />
          <div className="hub-comment-thread-line" />
        </div>
        <div className="hub-comment-content">
          <div className="hub-comment-header">
            <Link to={buildProfilePath(comment.author)} className="hub-comment-author">
              {comment.author?.username || comment.author?.name || 'Rebel'}
            </Link>
            <span className="hub-comment-time">{formatTimeAgo(comment.created_at)}</span>
          </div>
          
          {isEditing ? (
            <div className="hub-comment-edit">
              <MentionInput
                value={editText}
                onChange={setEditText}
                rows={3}
              />
              <div className="hub-comment-edit-actions">
                <button className="btn-primary btn-compact" onClick={handleSubmitEdit}>Save</button>
                <button className="btn-secondary btn-compact" onClick={() => setIsEditing(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <div className="hub-comment-body">{comment.body_md}</div>
          )}

          {comment.image_url && (
            <div className="hub-comment-image">
              <img src={comment.image_url} alt="" loading="lazy" />
            </div>
          )}

          <div className="hub-comment-actions">
            <VoteButtons score={comment.score} onVote={handleVote} userVote={comment.user_vote} />
            {depth < maxDepth && (
              <button className="hub-action-btn" onClick={() => setIsReplying(!isReplying)}>
                <MessageCircle className="w-3 h-3" /> Reply
              </button>
            )}
            {currentUserId === comment.author?.user_id && (
              <>
                <button className="hub-action-btn" onClick={() => setIsEditing(true)}>
                  <Pencil className="w-3 h-3" /> Edit
                </button>
                <button className="hub-action-btn danger" onClick={() => onDelete(comment.comment_id)}>
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </>
            )}
          </div>

          {isReplying && (
            <div className="hub-comment-reply">
              <MentionInput
                value={replyText}
                onChange={setReplyText}
                placeholder="Write a reply..."
                rows={3}
              />
              <div className="hub-comment-reply-actions">
                <button className="btn-primary btn-compact" onClick={handleSubmitReply}>Reply</button>
                <button className="btn-secondary btn-compact" onClick={() => setIsReplying(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {comment.children && comment.children.length > 0 && (
        <div className="hub-comment-children">
          {comment.children.map((child) => (
            <CommentItem
              key={child.comment_id}
              comment={child}
              depth={depth + 1}
              onVote={onVote}
              onReply={onReply}
              onEdit={onEdit}
              onDelete={onDelete}
              currentUserId={currentUserId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function buildCommentTree(comments) {
  const map = new Map();
  const roots = [];
  comments.forEach((c) => map.set(c.comment_id, { ...c, children: [] }));
  map.forEach((c) => {
    if (c.parent_comment_id && map.has(c.parent_comment_id)) {
      map.get(c.parent_comment_id).children.push(c);
    } else {
      roots.push(c);
    }
  });
  return roots;
}

export function SocialPostDetail() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user } = React.useContext(AuthContext);
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('top');
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const [postData, commentData] = await Promise.all([
          apiClient.get(`/api/hub/posts/${postId}`),
          apiClient.get(`/api/hub/posts/${postId}/comments`, { sort })
        ]);
        if (!active) return;
        setPost(postData);
        setEditTitle(postData.title || '');
        setEditBody(postData.body_md || '');
        setComments(commentData || []);
      } catch (err) {
        console.error('Load failed:', err);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [postId, sort]);

  const handlePostVote = async (vote) => {
    if (!user || !post) return;
    try {
      const result = await apiClient.post(`/api/hub/posts/${postId}/vote`, { vote });
      setPost(prev => ({ ...prev, score: result.score, user_vote: vote }));
    } catch (err) {
      console.error('Vote failed:', err);
    }
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim() || !user) return;
    try {
      const created = await apiClient.post('/api/hub/comments', {
        post_id: postId,
        body_md: newComment
      });
      setComments(prev => [created, ...prev]);
      setNewComment('');
    } catch (err) {
      console.error('Comment failed:', err);
    }
  };

  const handleCommentVote = async (commentId, vote) => {
    if (!user) return;
    try {
      await apiClient.post(`/api/hub/comments/${commentId}/vote`, { vote });
      const updated = await apiClient.get(`/api/hub/posts/${postId}/comments`, { sort });
      setComments(updated || []);
    } catch (err) {
      console.error('Vote failed:', err);
    }
  };

  const handleCommentReply = async (parentId, text) => {
    if (!user) return;
    try {
      await apiClient.post('/api/hub/comments', {
        post_id: postId,
        parent_comment_id: parentId,
        body_md: text
      });
      const updated = await apiClient.get(`/api/hub/posts/${postId}/comments`, { sort });
      setComments(updated || []);
    } catch (err) {
      console.error('Reply failed:', err);
    }
  };

  const handleCommentEdit = async (commentId, text) => {
    try {
      await apiClient.patch(`/api/hub/comments/${commentId}`, { body_md: text });
      const updated = await apiClient.get(`/api/hub/posts/${postId}/comments`, { sort });
      setComments(updated || []);
    } catch (err) {
      console.error('Edit failed:', err);
    }
  };

  const handleCommentDelete = async (commentId) => {
    try {
      await apiClient.del(`/api/hub/comments/${commentId}`);
      const updated = await apiClient.get(`/api/hub/posts/${postId}/comments`, { sort });
      setComments(updated || []);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handlePostUpdate = async () => {
    try {
      const updated = await apiClient.patch(`/api/hub/posts/${postId}`, {
        title: editTitle,
        body_md: editBody
      });
      setPost(prev => ({ ...prev, ...updated }));
      setIsEditing(false);
    } catch (err) {
      console.error('Update failed:', err);
    }
  };

  const handlePostDelete = async () => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await apiClient.del(`/api/hub/posts/${postId}`);
      navigate('/social');
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleDeleteAttachment = async (attachment) => {
    if (!attachment?.attachment_id) return;
    try {
      await apiClient.delete(`/api/hub/posts/${postId}/attachments/${attachment.attachment_id}`);
      const refreshed = await apiClient.get(`/api/hub/posts/${postId}`);
      setPost(refreshed);
    } catch (err) {
      console.error('Attachment delete failed:', err);
    }
  };

  if (loading) {
    return (
      <SocialHubLayout>
        <div className="hub-loading"><div className="spinner" /></div>
      </SocialHubLayout>
    );
  }

  if (!post) {
    return (
      <SocialHubLayout>
        <div className="hub-page">
          <Breadcrumbs items={[]} />
          <div className="hub-empty">
            <h3>Post not found</h3>
            <Link to="/social" className="btn-secondary mt-4">Back to Hub</Link>
          </div>
        </div>
      </SocialHubLayout>
    );
  }

  const commentTree = buildCommentTree(comments);

  return (
    <SocialHubLayout>
      <div className="hub-page hub-post-page">
        <Breadcrumbs items={[
          { label: post.guild_name || 'Community', to: `/social/communities/${post.guild_id}`, icon: Hash },
          { label: post.title.slice(0, 30) + (post.title.length > 30 ? '...' : '') }
        ]} />

        {/* Post */}
        <article className="hub-post-detail">
          <div className="hub-post-vote-col">
            <VoteButtons 
              score={post.score} 
              onVote={handlePostVote}
              userVote={post.user_vote}
              vertical
            />
          </div>
          
          <div className="hub-post-content">
            <div className="hub-post-meta">
              <Link to={`/social/communities/${post.guild_id}`} className="hub-community-link">
                <Hash className="w-3 h-3" />
                {post.guild_name || 'General'}
              </Link>
              <span className="meta-dot">•</span>
              <span>Posted by</span>
              <Link to={buildProfilePath(post.author)} className="hub-author-link">
                {post.author?.username || 'Rebel'}
              </Link>
              <span className="meta-dot">•</span>
              <span>{formatTimeAgo(post.created_at)}</span>
            </div>

            {isEditing ? (
              <div className="hub-post-edit">
                <input
                  className="input-glass mb-3"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  placeholder="Title"
                />
                <MentionInput
                  value={editBody}
                  onChange={setEditBody}
                  rows={6}
                />
                <div className="hub-post-edit-actions mt-3">
                  <button className="btn-primary" onClick={handlePostUpdate}>Save</button>
                  <button className="btn-secondary" onClick={() => setIsEditing(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <h1 className="hub-post-detail-title">{post.title}</h1>
                {post.body_md && <div className="hub-post-detail-body">{post.body_md}</div>}
              </>
            )}

            {post.image_url && (
              <div className="hub-post-detail-image">
                <img src={post.image_url} alt="" />
              </div>
            )}

            {Array.isArray(post.attachments) && post.attachments.length > 0 && (
              <PostAttachmentList
                attachments={post.attachments}
                editable={user?.user_id === post.author_id}
                onDelete={handleDeleteAttachment}
              />
            )}

            {post.embed?.data && (
              <Link 
                to={`/${post.embed.type === 'prompt' ? 'prompts' : post.embed.type === 'project' ? 'projects' : post.embed.type === 'instruction' ? 'agent-instructions' : 'folders'}/${post.embed.ref_id}`}
                className="hub-embed-card lg"
              >
                <div className="hub-embed-icon">
                  {post.embed.type === 'prompt' && <FileText className="w-5 h-5" />}
                  {post.embed.type === 'project' && <Box className="w-5 h-5" />}
                  {post.embed.type === 'folder' && <Folder className="w-5 h-5" />}
                  {post.embed.type === 'instruction' && <Bot className="w-5 h-5" />}
                </div>
                <div className="hub-embed-info">
                  <span className="hub-embed-type">{post.embed.type === 'instruction' ? 'Agent Instructions' : post.embed.type}</span>
                  <span className="hub-embed-name">{post.embed.data.name}</span>
                  {post.embed.data.description && (
                    <span className="hub-embed-desc">{post.embed.data.description}</span>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-gray-500" />
              </Link>
            )}

            {user?.user_id === post.author_id && !isEditing && (
              <div className="hub-post-owner-actions">
                <button className="btn-secondary btn-compact" onClick={() => setIsEditing(true)}>
                  <Pencil className="w-4 h-4" /> Edit
                </button>
                <button className="btn-secondary btn-compact danger" onClick={handlePostDelete}>
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            )}
          </div>
        </article>

        {/* Comments */}
        <section className="hub-comments-section">
          <div className="hub-comments-header">
            <h2>{comments.length} Comments</h2>
            <SelectMenu
              value={sort}
              options={[
                { value: 'top', label: 'Top' },
                { value: 'new', label: 'New' }
              ]}
              onChange={setSort}
              compact
            />
          </div>

          {user ? (
            <div className="hub-comment-composer">
              <UserAvatar user={user} size="md" />
              <div className="hub-comment-composer-input">
                <MentionInput
                  value={newComment}
                  onChange={setNewComment}
                  placeholder="What are your thoughts?"
                  rows={3}
                />
                <button 
                  className="btn-primary" 
                  onClick={handleSubmitComment}
                  disabled={!newComment.trim()}
                >
                  Comment
                </button>
              </div>
            </div>
          ) : (
            <div className="hub-comments-login">
              <p>Sign in to join the conversation</p>
              <Link to="/login" className="btn-primary">Log In</Link>
            </div>
          )}

          <div className="hub-comments-list">
            {commentTree.length === 0 ? (
              <div className="hub-comments-empty">
                <p>No comments yet. Be the first to share your thoughts!</p>
              </div>
            ) : (
              commentTree.map((c) => (
                <CommentItem
                  key={c.comment_id}
                  comment={c}
                  onVote={handleCommentVote}
                  onReply={handleCommentReply}
                  onEdit={handleCommentEdit}
                  onDelete={handleCommentDelete}
                  currentUserId={user?.user_id}
                />
              ))
            )}
          </div>
        </section>
      </div>
    </SocialHubLayout>
  );
}

export function SocialNotifications() {
  const { user } = React.useContext(AuthContext);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    apiClient.get('/api/hub/notifications')
      .then(data => setNotifications(data || []))
      .catch(() => setNotifications([]))
      .finally(() => setLoading(false));
  }, [user]);

  const markAsRead = async (notificationId) => {
    try {
      await apiClient.post('/api/hub/notifications/read', { notification_ids: [notificationId] });
      setNotifications(prev => prev.map(n => 
        n.notification_id === notificationId ? { ...n, is_read: true } : n
      ));
    } catch (err) {
      console.error('Mark read failed:', err);
    }
  };

  return (
    <SocialHubLayout>
      <div className="hub-page">
        <Breadcrumbs items={[{ label: 'Notifications', icon: Bell }]} />
        
        <section className="hub-page-header">
          <div>
            <h1>Notifications</h1>
            <p>Replies, mentions, and updates from the community.</p>
          </div>
        </section>

        {loading ? (
          <div className="hub-loading"><div className="spinner" /></div>
        ) : notifications.length === 0 ? (
          <div className="hub-empty">
            <Bell className="w-12 h-12 text-gray-600" />
            <h3>No notifications</h3>
            <p>You're all caught up!</p>
          </div>
        ) : (
          <div className="hub-notifications-list">
            {notifications.map((n) => (
              <div 
                key={n.notification_id} 
                className={`hub-notification ${n.is_read ? 'read' : 'unread'}`}
                onClick={() => !n.is_read && markAsRead(n.notification_id)}
              >
                <div className="hub-notification-icon">
                  {n.type === 'comment' && <MessageCircle className="w-4 h-4" />}
                  {n.type === 'mention' && <AtSign className="w-4 h-4" />}
                  {n.type === 'vote' && <ArrowUp className="w-4 h-4" />}
                </div>
                <div className="hub-notification-content">
                  <p>{n.type === 'comment' ? 'Someone replied to your post' : n.type}</p>
                  <span className="hub-notification-time">{formatTimeAgo(n.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </SocialHubLayout>
  );
}
